import { useState, useRef, useEffect, useMemo } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Play, Upload, Download, FileSpreadsheet, Trash2, Link2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { FileImportModal, type FileImportData } from '@/components/FileImportModal'
import {
  useKAGenDoc,
  useUpdateKAGen,
  useDeleteKAGen,
  useGenerateKACodes,
  useLoadKAFromFile,
} from '@/hooks/useKAGeneration'
import { kaTemplatesApi, kaGenerationApi, settingsApi } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import {
  collectManualAIs,
  collectAutoAIs,
  aiInputHint,
  autoSourceLabel,
} from '@/lib/ka-template-fields'
import type { KAGenCodeType } from '@/types'
import type { KATemplate } from '@/lib/api'

const CODE_TYPE_OPTIONS: { value: KAGenCodeType; label: string }[] = [
  { value: 'ka_box', label: 'Упаковка (КА на ящик)' },
  { value: 'ka_pallet', label: 'Паллета (КА на паллету)' },
  { value: 'ka_box_pallet', label: 'Упаковка + Паллета' },
]

export function KAGenerationDocPage() {
  const { genId } = useParams<{ genId: string }>()
  const navigate = useNavigate()

  const { data: doc, isLoading } = useKAGenDoc(genId)
  const updateMutation = useUpdateKAGen(genId ?? '')
  const deleteMutation = useDeleteKAGen()
  const generateMutation = useGenerateKACodes(genId ?? '')
  const loadFileMutation = useLoadKAFromFile(genId ?? '')

  const [importModalOpen, setImportModalOpen] = useState(false)

  // Load KA templates for dropdowns
  const { data: boxTemplatesRes } = useQuery({
    queryKey: ['ka-templates', 'ka_box'],
    queryFn: () => kaTemplatesApi.getByType('ka_box'),
  })
  const { data: palletTemplatesRes } = useQuery({
    queryKey: ['ka-templates', 'ka_pallet'],
    queryFn: () => kaTemplatesApi.getByType('ka_pallet'),
  })

  const boxTemplates: KATemplate[] = Array.isArray(boxTemplatesRes?.data)
    ? boxTemplatesRes!.data
    : ((boxTemplatesRes?.data as unknown as { data?: KATemplate[] })?.data) ?? []
  const palletTemplates: KATemplate[] = Array.isArray(palletTemplatesRes?.data)
    ? palletTemplatesRes!.data
    : ((palletTemplatesRes?.data as unknown as { data?: KATemplate[] })?.data) ?? []

  // Local state for number inputs (debounced save to prevent per-keystroke API calls)
  const [localQuantity, setLocalQuantity] = useState<number>(1)
  const [localBoxesPerPallet, setLocalBoxesPerPallet] = useState<number>(1)
  const [localManualAI, setLocalManualAI] = useState<Record<string, string>>({})
  const [highlightedPalletNum, setHighlightedPalletNum] = useState<number | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const manualSaveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (doc) {
      setLocalQuantity(doc.quantity)
      setLocalBoxesPerPallet(doc.boxes_per_pallet ?? 1)
      setLocalManualAI(doc.manual_ai_values ?? {})
    }
  }, [doc?.quantity, doc?.boxes_per_pallet, doc?.manual_ai_values])

  const { data: gs1Prefix } = useQuery({
    queryKey: ['settings', 'gs1-prefix'],
    queryFn: async () => {
      const res = await settingsApi.get()
      const data = res.data as { gs1_company_prefix?: string }
      return (data.gs1_company_prefix ?? '').trim()
    },
  })

  const needsBox = doc?.code_type === 'ka_box' || doc?.code_type === 'ka_box_pallet'
  const needsPallet = doc?.code_type === 'ka_pallet' || doc?.code_type === 'ka_box_pallet'

  const { data: selectedBoxTemplate } = useQuery({
    queryKey: ['ka-template', doc?.ka_template_box],
    queryFn: async () => {
      const res = await kaTemplatesApi.getById(doc!.ka_template_box)
      return (res.data as KATemplate) ?? null
    },
    enabled: !!doc?.ka_template_box && needsBox,
  })
  const { data: selectedPalletTemplate } = useQuery({
    queryKey: ['ka-template', doc?.ka_template_pallet],
    queryFn: async () => {
      const res = await kaTemplatesApi.getById(doc!.ka_template_pallet)
      return (res.data as KATemplate) ?? null
    },
    enabled: !!doc?.ka_template_pallet && needsPallet,
  })

  const manualAIFields = useMemo(
    () => collectManualAIs([selectedBoxTemplate, selectedPalletTemplate]),
    [selectedBoxTemplate, selectedPalletTemplate],
  )
  const autoAIFields = useMemo(
    () => collectAutoAIs([selectedBoxTemplate, selectedPalletTemplate]),
    [selectedBoxTemplate, selectedPalletTemplate],
  )

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      if (manualSaveTimerRef.current) clearTimeout(manualSaveTimerRef.current)
    }
  }, [])

  const debouncedSave = (field: string, value: number) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      updateMutation.mutate({ [field]: value })
    }, 600)
  }

  const debouncedSaveManualAI = (values: Record<string, string>) => {
    if (manualSaveTimerRef.current) clearTimeout(manualSaveTimerRef.current)
    manualSaveTimerRef.current = setTimeout(() => {
      updateMutation.mutate({ manual_ai_values: values })
    }, 600)
  }

  const handleManualAIChange = (ai: string, value: string) => {
    const next = { ...localManualAI, [ai]: value }
    setLocalManualAI(next)
    debouncedSaveManualAI(next)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  if (!doc) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p className="text-lg">Документ не найден</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate('/admin/ka-generation')}>
          Назад к списку
        </Button>
      </div>
    )
  }

  const isDraft = doc.status === 'draft'
  const isGenerated = doc.status === 'generated'

  const handleFieldChange = (field: string, value: string | number) => {
    updateMutation.mutate({ [field]: value })
  }

  const handleGenerate = () => {
    generateMutation.mutate()
  }

  const handleLoadFile = (data: FileImportData) => {
    const formData = new FormData()
    formData.append('file_boxes', data.fileBoxes)
    if (data.filePallets) {
      formData.append('file_pallets', data.filePallets)
    }
    formData.append('config', JSON.stringify(data.config))
    loadFileMutation.mutate(formData, {
      onSuccess: () => setImportModalOpen(false),
    })
  }

  const handleDelete = () => {
    if (!confirm('Удалить этот документ?')) return
    deleteMutation.mutate(doc.id, {
      onSuccess: () => navigate('/admin/ka-generation'),
    })
  }

  // Build pallet lookup: num → pallet aggregate
  const palletMap = new Map(
    (doc.aggregates ?? []).filter((a) => a.level === 'pallet').map((a) => [a.num, a]),
  )

  // Rows for unified table
  const tableRows = (doc.aggregates ?? []).filter((a) =>
    needsBox ? a.level === 'box' : a.level === 'pallet',
  )

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/ka-generation')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{doc.id}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <StatusBadge
                {...(doc.status === 'draft'
                  ? { label: 'Черновик', color: 'bg-gray-100', textColor: 'text-gray-700' }
                  : { label: 'Сгенерирован', color: 'bg-green-100', textColor: 'text-green-700' })}
              />
              {doc.source && (
                <span className="text-xs text-gray-400">
                  {doc.source === 'loaded_from_file' ? 'из файла' : 'сгенерирован'}
                </span>
              )}
              <span className="text-xs text-gray-400">{formatDateTime(doc.created_at)}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isDraft && (
            <Button size="sm" variant="danger" onClick={handleDelete} isLoading={deleteMutation.isPending}>
              <Trash2 className="w-4 h-4 mr-1" /> Удалить
            </Button>
          )}
        </div>
      </div>

      {/* Draft Form */}
      {isDraft && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Параметры</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Code Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Тип КА-кода</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={doc.code_type}
                onChange={(e) => handleFieldChange('code_type', e.target.value)}
              >
                {CODE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {doc.code_type === 'ka_box_pallet' ? 'Кол-во паллет' : 'Количество'}
              </label>
              <input
                type="number"
                className="w-full border rounded-md px-3 py-2 text-sm"
                min={1}
                max={10000}
                value={localQuantity}
                onChange={(e) => {
                  const v = parseInt(e.target.value) || 1
                  setLocalQuantity(v)
                  debouncedSave('quantity', v)
                }}
              />
            </div>

            {/* Boxes per Pallet (only for ka_box_pallet) */}
            {doc.code_type === 'ka_box_pallet' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Упаковок в паллете</label>
                <input
                  type="number"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  min={1}
                  max={1000}
                  value={localBoxesPerPallet}
                  onChange={(e) => {
                    const v = parseInt(e.target.value) || 1
                    setLocalBoxesPerPallet(v)
                    debouncedSave('boxes_per_pallet', v)
                  }}
                />
              </div>
            )}

            {/* Box Template */}
            {needsBox && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Шаблон КА упаковки</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={doc.ka_template_box}
                  onChange={(e) => handleFieldChange('ka_template_box', e.target.value)}
                >
                  <option value="">Без шаблона (только SSCC)</option>
                  {boxTemplates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.template_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Pallet Template */}
            {needsPallet && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Шаблон КА паллеты</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={doc.ka_template_pallet}
                  onChange={(e) => handleFieldChange('ka_template_pallet', e.target.value)}
                >
                  <option value="">Без шаблона (только SSCC)</option>
                  {palletTemplates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.template_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isDraft && !gs1Prefix && (
            <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
              Для генерации SSCC нужен GS1 префикс компании.{' '}
              <Link to="/admin/infrastructure" className="font-medium underline hover:text-amber-700">
                Укажите в инфраструктуре
              </Link>
            </div>
          )}

          {autoAIFields.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {autoAIFields.map((entry) => (
                <span
                  key={entry.ai}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-xs border border-blue-100"
                >
                  <span className="font-mono">({entry.ai})</span>
                  <span>{entry.label}</span>
                  <span className="text-blue-600 font-medium">{autoSourceLabel(entry.source)}</span>
                </span>
              ))}
            </div>
          )}

          {manualAIFields.length > 0 && (
            <div className="mt-6 pt-4 border-t">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Значения AI (ручной ввод)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {manualAIFields.map((entry) => (
                  <div key={entry.ai}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ({entry.ai}) {entry.label}
                      {entry.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                      value={localManualAI[entry.ai] ?? entry.default_value ?? ''}
                      placeholder={aiInputHint(entry.ai) || 'Значение'}
                      onChange={(e) => handleManualAIChange(entry.ai, e.target.value)}
                    />
                    {aiInputHint(entry.ai) && (
                      <p className="text-xs text-gray-400 mt-0.5">{aiInputHint(entry.ai)}</p>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                AI 00 (SSCC) и AI 21 (серийник) генерируются автоматически при нажатии «Сгенерировать».
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <Button onClick={handleGenerate} isLoading={generateMutation.isPending}>
              <Play className="w-4 h-4 mr-1" /> Сгенерировать
            </Button>

            <Button variant="secondary" onClick={() => setImportModalOpen(true)}>
              <Upload className="w-4 h-4 mr-1" /> Загрузить из файла
            </Button>
          </div>

          {/* File Import Modal */}
          <FileImportModal
            isOpen={importModalOpen}
            onClose={() => setImportModalOpen(false)}
            needsTwoFiles={doc.code_type === 'ka_box_pallet'}
            codeType={doc.code_type}
            onSubmit={handleLoadFile}
            isSubmitting={loadFileMutation.isPending}
          />
        </div>
      )}

      {/* Generated: Export + Summary */}
      {isGenerated && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Результат</h2>
            <div className="flex gap-2">
              <a
                href={kaGenerationApi.exportCsvUrl(doc.id)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
              >
                <Download className="w-4 h-4" /> CSV
              </a>
              <a
                href={kaGenerationApi.exportExcelUrl(doc.id)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
              >
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Тип:</span>{' '}
              <span className="font-medium">
                {CODE_TYPE_OPTIONS.find((o) => o.value === doc.code_type)?.label ?? doc.code_type}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Количество:</span>{' '}
              <span className="font-medium">{doc.quantity}</span>
            </div>
            {doc.ka_boxes_count > 0 && (
              <div>
                <span className="text-gray-500">Упаковок:</span>{' '}
                <span className="font-medium">{doc.ka_boxes_count}</span>
              </div>
            )}
            {doc.ka_pallets_count > 0 && (
              <div>
                <span className="text-gray-500">Паллет:</span>{' '}
                <span className="font-medium">{doc.ka_pallets_count}</span>
              </div>
            )}
            {doc.generated_at && (
              <div>
                <span className="text-gray-500">Сгенерировано:</span>{' '}
                <span className="font-medium">{formatDateTime(doc.generated_at)}</span>
              </div>
            )}
            {doc.linked_task_id && (
              <div className="flex items-center gap-1">
                <Link2 className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-500">Задание:</span>{' '}
                <span className="font-medium font-mono text-gray-800">{doc.linked_task_id}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table: КА упаковки, КА паллеты, Задание */}
      {isGenerated && tableRows.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Коды</h2>
            <span className="text-sm text-gray-500">{tableRows.length} записей</span>
          </div>
          <div className="overflow-auto max-h-[32rem]">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {needsBox && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">КА упаковки</th>
                  )}
                  {needsPallet && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">КА паллеты</th>
                  )}
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Задание</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableRows.map((agg) => {
                  const parentPallet = agg.pallet_num != null ? palletMap.get(agg.pallet_num) : undefined
                  const palletNum = agg.level === 'pallet' ? agg.num : agg.pallet_num
                  const isHighlighted = highlightedPalletNum != null && palletNum === highlightedPalletNum
                  return (
                    <tr
                      key={agg.aggregate_id}
                      className={isHighlighted ? 'bg-blue-50' : ''}
                    >
                      {needsBox && (
                        <td
                          className="px-3 py-2 font-mono text-xs cursor-pointer hover:text-blue-600"
                          title={agg.sscc}
                          onClick={() => {
                            if (agg.pallet_num != null) {
                              setHighlightedPalletNum(prev => prev === agg.pallet_num ? null : agg.pallet_num!)
                            }
                          }}
                        >
                          {agg.ka || agg.sscc}
                        </td>
                      )}
                      {needsPallet && (
                        <td
                          className="px-3 py-2 font-mono text-xs cursor-pointer hover:text-blue-600"
                          onClick={() => {
                            if (palletNum != null) {
                              setHighlightedPalletNum(prev => prev === palletNum ? null : palletNum)
                            }
                          }}
                        >
                          {agg.level === 'pallet'
                            ? (agg.ka || agg.sscc)
                            : parentPallet
                              ? (parentPallet.ka || parentPallet.sscc)
                              : '—'}
                        </td>
                      )}
                      <td className="px-3 py-2 text-xs">
                        {agg.linked_task_id ? (
                          <span className="font-mono text-gray-700">{agg.linked_task_id}</span>
                        ) : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
